import {
  cloneElement,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { VFlex } from '../base';

export type AccordionProps = {
  anchor: (isOpened: boolean) => ReactElement;
  content: ReactNode | ((isOpened: boolean) => ReactNode);
  duration?: number; // ms
  initialOpen?: boolean;
  onChange?: (isOpened: boolean) => void;
  disabled?: boolean;
  status?: boolean;
};

enum OPEN_STATE {
  CLOSED,
  OPENNING,
  OPENED,
  CLOSING,
}

const getDeterminatedState = (state: OPEN_STATE): boolean => {
  switch (state) {
    case OPEN_STATE.CLOSED:
    case OPEN_STATE.CLOSING:
      return false;
    default:
      return true;
  }
};

const Accordion = (props: AccordionProps) => {
  const timeOut = useRef<NodeJS.Timeout | null>(null);
  const init = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const duration = props.duration || 200;
  const [openState, setOpenState] = useState<OPEN_STATE>(
    typeof props.status === 'undefined'
      ? typeof props.initialOpen === 'undefined'
        ? OPEN_STATE.CLOSED
        : props.initialOpen
          ? OPEN_STATE.OPENED
          : OPEN_STATE.CLOSED
      : props.status
        ? OPEN_STATE.OPENED
        : OPEN_STATE.CLOSED,
  );

  const [contentHeight, setContentHeight] = useState(
    props.status ? 'auto' : '0px',
  );

  const getCurrentContentHeight = () => {
    return `${contentRef.current?.getBoundingClientRect().height || 0}px`;
  };

  const onClickAnchor = useCallback(
    (status: boolean, initMode?: boolean) => {
      if (props.disabled) return;
      if (status) {
        if (timeOut.current) clearTimeout(timeOut.current);
        setOpenState(OPEN_STATE.OPENNING);
        const contentHeight = getCurrentContentHeight();
        setContentHeight(contentHeight);
        timeOut.current = setTimeout(() => {
          setOpenState(OPEN_STATE.OPENED);
          setContentHeight('auto');
        }, duration);
      } else {
        if (timeOut.current) clearTimeout(timeOut.current);
        if (initMode) {
          setOpenState(OPEN_STATE.CLOSED);
          setContentHeight('0px');
        } else {
          setOpenState(OPEN_STATE.CLOSING);
          setContentHeight(getCurrentContentHeight());
          timeOut.current = setTimeout(() => {
            setContentHeight('0px');
          }, 10);
          timeOut.current = setTimeout(() => {
            setOpenState(OPEN_STATE.CLOSED);
          }, duration);
        }
      }
    },
    [duration, setOpenState],
  );

  const clonedAnchor = useMemo(() => {
    return cloneElement(props.anchor(getDeterminatedState(openState)), {
      onClick: () => {
        onClickAnchor(!getDeterminatedState(openState));
        setTimeout(() => {
          props.onChange?.(!getDeterminatedState(openState));
        }, duration);
      },
    });
  }, [duration, onClickAnchor, openState, props]);

  useEffect(() => {
    if (init.current === false && typeof props.initialOpen !== 'undefined') {
      onClickAnchor(props.initialOpen, !init.current);
      init.current = true;
    }
  }, [duration, onClickAnchor, props.initialOpen]);

  useEffect(() => {
    if (typeof props.status !== 'undefined') {
      onClickAnchor(props.status, false);
    }
  }, [duration, onClickAnchor, props.status]);

  return (
    <>
      {clonedAnchor}
      <AccordionContent
        duration={duration}
        isOpened={getDeterminatedState(openState)}
        contentHeight={contentHeight}
      >
        <div ref={contentRef}>
          {typeof props.content === 'function'
            ? props.content(getDeterminatedState(openState))
            : props.content}
        </div>
      </AccordionContent>
    </>
  );
};

type AccordionStyleProps = {
  isOpened: boolean;
  contentHeight: string;
  duration: number;
};

const AccordionContent = styled(VFlex).attrs<AccordionStyleProps>((props) => ({
  style: {
    transition: `all ${props.duration}ms`,
    overflow: 'hidden',
    height: props.contentHeight,
    opacity: props.isOpened ? 1 : 0,
  },
}))<AccordionStyleProps>``;

export default Accordion;
